import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import { panelHozir, soatFarqi } from '../lib/agent';
import { SILENT_ERROR } from '../lib/queryMeta';

/** Soat farqi shu oraliqda qayta o'lchanadi. */
const FARQ_ORALIQ_MS = 10 * 60_000;
/** Ma'lumot o'zgarmasa ham ("Aloqa yo'q — N daq") belgilar shu oraliqda qayta hisoblanadi (sahifa ko'rinib tursa). */
const TIK_MS = 15_000;

/**
 * Panelning "hozir" i SERVER soati bo'yicha (ms) — agent holat belgilari (`agentHolati`) uchun.
 *
 * Panel ko'pincha SATA ilovasi ichida, o'sha maktab kompyuterida ochiladi; uning soati noto'g'ri
 * bo'lsa `Date.now() - holatAt` (server vaqti) yolg'on chiqadi va hamma agent "Aloqa yo'q"
 * ko'rinardi (Review Focus 2). Shuning uchun `GET /health` dagi `timestamp` bilan farq o'lchanadi
 * (mount'da va har 10 daqiqada; kalit umumiy — bir nechta komponent bitta so'rov). Xato yoki
 * yaroqsiz javobda farq 0 (oldingi o'lchov bo'lsa — o'sha qoladi).
 *
 * "hozir" = oxirgi tik va agentlar ro'yxati kelgan payt (`malumotVaqti` — o'sha so'rovning react-query
 * `dataUpdatedAt` i) dan kechrog'i (`panelHozir`). Tik 15 s da qayta chizadi: so'rov natijasi o'zgarmasa react-query
 * qayta chizmaydi, jim qolgan kompyuter esa aynan shunday — uning "Aloqa yo'q" belgisi kechikmasin.
 *
 * Sahifa YASHIRIN bo'lsa (boshqa tab, yig'ilgan oyna) tik to'xtaydi va "hozir" muzlaydi: ro'yxat
 * so'rovlari ham fonda yangilanmaydi (react-query `refetchInterval` faqat ko'rinib turgan sahifada).
 * Aks holda qaytilganda eski ma'lumot + yangi "hozir" sog' kompyuterlarni ham yolg'on "Aloqa yo'q"
 * qilardi. Qaytganda "hozir" ro'yxat bilan BIRGA yangilanadi: `refetchOnWindowFocus` olib kelgan yangi
 * ma'lumotning `dataUpdatedAt` i uni darhol (tikni kutmasdan) suradi, u kelguncha esa "hozir" eski ro'yxatga
 * mos qoladi. Tik qaytgandan 15 s keyin qayta boshlanadi. Faqat tikka tayanilganda (tik har `visibilitychange`
 * da qaytadan sanaladi) sahifa har safar 15 s dan kam ko'rinsa "hozir" umuman siljimas, fonda o'chgan
 * kompyuter uzoq vaqt yolg'on "Ishlayapti" qolardi.
 *
 * Sahifaga keshdagi ro'yxat bilan qaytilganda (react-query gcTime ichida) ham shu qoida: boshlang'ich tik — ro'yxat
 * kelgan payt, yangi ro'yxat kelguncha "hozir" eski ro'yxatga mos qoladi (pastda).
 */
export function useServerSoati(malumotVaqti: number): number {
  const { data: farq = 0 } = useQuery({
    queryKey: ['server-soati'],
    queryFn: async () => {
      const boshi = Date.now();
      const r = await api.get<{ timestamp?: unknown }>('/health');
      return soatFarqi(r.data?.timestamp, boshi, Date.now());
    },
    staleTime: FARQ_ORALIQ_MS,
    refetchInterval: FARQ_ORALIQ_MS,
    retry: false,
    // Farq o'lchanmasa brauzer soati ishlatiladi — foydalanuvchiga toast kerak emas
    meta: SILENT_ERROR,
  });

  // Boshlang'ich tik — ro'yxat kelgan payt, joriy "hozir" emas: sahifaga >2 daqiqadan keyin (react-query keshi — gcTime —
  // ichida) qaytilganda keshdagi eski ro'yxat darhol chiziladi va joriy vaqt bilan baholansa sog' kompyuterlar ham yangi
  // ro'yxat kelguncha bir lahza yolg'on "Aloqa yo'q" bo'lardi. Ro'yxat O'Z vaqti bilan baholanadi, yangisi (eskirgan
  // ro'yxat mount'da qayta so'raladi) "hozir"ni o'zi suradi. Ma'lumot hali yo'q (0) — joriy vaqt.
  const [tik, setTik] = useState(() => malumotVaqti || Date.now());
  useEffect(() => {
    let taymer: ReturnType<typeof setInterval> | undefined;
    const tikniBoshla = () => {
      clearInterval(taymer);
      taymer = undefined;
      if (document.visibilityState === 'hidden') return;
      taymer = setInterval(() => setTik(Date.now()), TIK_MS);
    };
    tikniBoshla();
    document.addEventListener('visibilitychange', tikniBoshla);
    return () => {
      clearInterval(taymer);
      document.removeEventListener('visibilitychange', tikniBoshla);
    };
  }, []);

  return panelHozir(tik, malumotVaqti) + farq;
}
